import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ExternalLinkIcon, CheckIcon } from 'lucide-react';
interface PortfolioModalProps {
  project: Project;
  onClose: () => void;
}
interface Project {
  id: number;
  title: string;
  category: string;
  quote: string;
  client: string;
  image: string;
  challenge: string;
  solution: string;
  results: string[];
  techStack: string[];
  link: string;
}
export function PortfolioModal({
  project,
  onClose
}: PortfolioModalProps) {
  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);
  return <AnimatePresence data-id="element-1094">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose} data-id="element-1095">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} transition={{
        type: 'spring',
        damping: 25
      }} className="bg-white dark:bg-[#1E1E1E] rounded-xl overflow-hidden max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1096">
          {/* Main Image */}
          <div className="relative h-[40vh] md:h-[50vh] bg-gray-100 dark:bg-gray-900" data-id="element-1097">
            <img src={project.image} alt={`${project.title} website screenshot — Bright Pixxel Studios case study`} className="w-full h-full object-cover" data-id="element-1098" />
            {/* Close button */}
            <button className="absolute top-4 right-4 bg-white/80 dark:bg-black/50 p-2 rounded-full" onClick={onClose} aria-label="Close modal" data-id="element-1099">
              <XIcon size={20} data-id="element-1100" />
            </button>
          </div>
          {/* Content */}
          <div className="p-6 md:p-8" data-id="element-1101">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6" data-id="element-1102">
              <div data-id="element-1103">
                <span className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-2" data-id="element-1104">
                  {project.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-bold" data-id="element-1105">
                  {project.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 italic mt-1" data-id="element-1106">
                  {project.client}
                </p>
              </div>
              <a href={project.link} target="_blank" rel="noopener noreferrer" className="flex items-center bg-[#3AA6F9] hover:bg-[#2A95E8] text-white px-4 py-2 rounded-md transition-colors" data-id="element-1107">
                <span data-id="element-1108">Visit Website</span>
                <ExternalLinkIcon size={16} className="ml-2" data-id="element-1109" />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1110">
              <div data-id="element-1111">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1112">The Challenge</h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1113">
                  {project.challenge}
                </p>
              </div>
              <div data-id="element-1114">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1115">Our Solution</h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1116">
                  {project.solution}
                </p>
              </div>
            </div>
            <div className="mb-8" data-id="element-1117">
              <h3 className="text-xl font-semibold mb-3" data-id="element-1118">Results</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1119">
                {project.results.map((result, index) => <div key={index} className="flex items-start" data-id="element-1120">
                    <CheckIcon size={18} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-1121" />
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-1122">{result}</p>
                  </div>)}
              </div>
            </div>
            <div data-id="element-1123">
              <h3 className="text-xl font-semibold mb-3" data-id="element-1124">Technology Stack</h3>
              <div className="flex flex-wrap gap-2" data-id="element-1125">
                {project.techStack.map((tech, index) => <span key={index} className="bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm" data-id="element-1126">
                    {tech}
                  </span>)}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}